/**
 * TeamStrength Hub - SuperAdmin Page
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { orgApi, superadminApi, userApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Building2, Plus, MoreVertical, Users, Layers, Edit2, 
  Power, PowerOff, Search, Shield, AlertTriangle
} from 'lucide-react';

const SuperAdminPage = () => {
  const { isSuperAdmin, user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [creating, setCreating] = useState(false);
  
  const [newOrg, setNewOrg] = useState({
    name: '',
    notes: '',
    contracted_team_slots: 5
  });

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await orgApi.list();
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const handleCreateOrg = async () => {
    if (!newOrg.name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setCreating(true);
    try {
      await orgApi.create(newOrg);
      toast.success('Organization created!');
      setShowCreateDialog(false);
      setNewOrg({ name: '', notes: '', contracted_team_slots: 5 });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateOrg = async () => {
    if (!selectedOrg) return;

    try {
      await orgApi.update(selectedOrg.org_id, selectedOrg);
      toast.success('Organization updated!');
      setShowEditDialog(false);
      setSelectedOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update organization');
    }
  };

  const handleToggleStatus = async (org) => {
    const newStatus = org.status === 'active' ? 'inactive' : 'active';
    try {
      await orgApi.update(org.org_id, { status: newStatus });
      toast.success(`Organization ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (org) => {
    setSelectedOrg({ ...org });
    setShowEditDialog(true);
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
            Access Denied
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-md">
            You don't have SuperAdmin privileges. Contact your administrator if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superadmin-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-violet-600" />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
              SuperAdmin
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage all organizations and customers
          </p>
        </div>
        <Button 
          className="bg-violet-600 hover:bg-violet-700"
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-org-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Organization
        </Button>
      </div>

      {/* Organizations Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 skeleton rounded-xl" />
          ))}
        </div>
      ) : organizations.length === 0 ? (
        <Card className="animate-fade-in">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
              No organizations yet
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
              Create your first organization to get started.
            </p>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Organization
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in stagger-1">
          {organizations.map((org) => (
            <Card key={org.org_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                        {org.name}
                      </CardTitle>
                      <Badge 
                        variant={org.status === 'active' ? 'default' : 'secondary'}
                        className="mt-1 capitalize"
                      >
                        {org.status}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(org)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(org)}>
                        {org.status === 'active' ? (
                          <>
                            <PowerOff className="w-4 h-4 mr-2" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="w-4 h-4 mr-2" />
                            Activate
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Team Slots
                    </span>
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {org.contracted_team_slots}
                    </span>
                  </div>
                  {org.notes && (
                    <p className="text-sm text-slate-500 line-clamp-2">
                      {org.notes}
                    </p>
                  )}
                  <p className="text-xs text-slate-400">
                    Created: {new Date(org.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create Organization</DialogTitle>
            <DialogDescription>
              Set up a new customer organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                placeholder="Acme Corporation"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-slots">Contracted Team Slots</Label>
              <Input
                id="org-slots"
                type="number"
                min="1"
                value={newOrg.contracted_team_slots}
                onChange={(e) => setNewOrg({ ...newOrg, contracted_team_slots: parseInt(e.target.value) || 1 })}
                data-testid="org-slots-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-notes">Notes (Optional)</Label>
              <Textarea
                id="org-notes"
                placeholder="Additional notes about this organization..."
                value={newOrg.notes}
                onChange={(e) => setNewOrg({ ...newOrg, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleCreateOrg}
              disabled={creating}
              data-testid="confirm-create-org-btn"
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Edit Organization</DialogTitle>
            <DialogDescription>
              Update organization details
            </DialogDescription>
          </DialogHeader>
          {selectedOrg && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={selectedOrg.name}
                  onChange={(e) => setSelectedOrg({ ...selectedOrg, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Contracted Team Slots</Label>
                <Input
                  type="number"
                  min="1"
                  value={selectedOrg.contracted_team_slots}
                  onChange={(e) => setSelectedOrg({ ...selectedOrg, contracted_team_slots: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={selectedOrg.notes || ''}
                  onChange={(e) => setSelectedOrg({ ...selectedOrg, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleUpdateOrg}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
